/**
 * ALL IS WELL PATHOLOGY SERVICE
 * ─────────────────────────────────────────────────────────────────────────────
 * ARCHITECTURE:
 *   PATIENT → ALL IS WELL EXPERIENCE → SECURE BACKEND → PATHOLOGY SERVICE → LIS
 *
 * CURRENT MODE: DEMO — uses demo patient and demo lab report data.
 * FUTURE MODE:  Replace getPatientByMobile() and getReportsForPatient() with
 *               calls to the real All Is Well Pathology/LIS API.
 *
 * ⚠️  ALL DATA BELOW IS DEMO/PROTOTYPE DATA ONLY.
 *     Never use real patient records, real lab results, or real medical data.
 *     In production, all data must be retrieved from a secure authenticated backend.
 * ─────────────────────────────────────────────────────────────────────────────
 */

// ─── Data Models ──────────────────────────────────────────────────────────────

/** Represents a patient identity record (separate from report data). */
export interface PatientRecord {
  patientId: string;       // Internal All Is Well patient ID
  hospitalMrn: string;     // Hospital Medical Record Number
  fullName: string;
  firstName: string;
  mobile: string;          // Registered mobile (masked for display)
  isDemo: boolean;
}

/** Represents a single lab report record. */
export interface LabReport {
  reportId: string;
  patientId: string;       // Foreign key → PatientRecord.patientId
  testName: string;
  testDate: string;        // Display date e.g. "18 August 2026"
  centreId: string;
  centreName: string;
  labId: string;
  labName: string;
  status: 'available' | 'pending' | 'processing';
  reportFileUrl: string | null;  // null until secure backend is connected
  category: string;
  isDemo: boolean;
  createdAt: string;
  updatedAt: string;
}

/** Audit log entry for report access events. */
export interface ReportAuditEntry {
  patientId: string;
  reportId: string;
  action: 'REPORT_VIEWED' | 'REPORT_DOWNLOADED' | 'REPORT_REQUESTED' | 'REPORT_SENT_WHATSAPP';
  timestamp: string;
  channel: 'WEBSITE' | 'WHATSAPP' | 'PASSPORT';
}

/** Result of patient verification. */
export interface PatientVerificationResult {
  success: boolean;
  patient: PatientRecord | null;
  error?: string;
}

/** Result of fetching reports for a patient. */
export interface PatientReportsResult {
  success: boolean;
  reports: LabReport[];
  error?: string;
}

// ─── Demo Data ────────────────────────────────────────────────────────────────
// ⚠️ DEMO DATA — clearly labelled, never real patient records.

export const DEMO_PATIENT_RECORD: PatientRecord = {
  patientId: 'AIW-DEMO-000124',
  hospitalMrn: 'DEMO-MRN-00124',
  fullName: 'Rahul Sharma',
  firstName: 'Rahul',
  mobile: '98XXXXXXXX',
  isDemo: true,
};

/** Demo mobile number that triggers the demo patient flow. */
export const DEMO_MOBILE_NUMBER = '9800000000';

/** Demo OTP for prototype verification. */
export const DEMO_OTP = '123456';

export const DEMO_LAB_REPORTS: LabReport[] = [
  {
    reportId: 'RPT-DEMO-001',
    patientId: 'AIW-DEMO-000124',
    testName: 'CBC (Complete Blood Count)',
    testDate: '18 August 2026',
    centreId: 'centre-burhanpur',
    centreName: 'All Is Well Diagnostics, Burhanpur',
    labId: 'LAB-AIW-001',
    labName: 'All Is Well Pathology',
    status: 'available',
    reportFileUrl: null,
    category: 'Blood Tests',
    isDemo: true,
    createdAt: '2026-08-18T09:00:00Z',
    updatedAt: '2026-08-18T11:30:00Z',
  },
  {
    reportId: 'RPT-DEMO-002',
    patientId: 'AIW-DEMO-000124',
    testName: 'Lipid Profile',
    testDate: '18 August 2026',
    centreId: 'centre-burhanpur',
    centreName: 'All Is Well Diagnostics, Burhanpur',
    labId: 'LAB-AIW-001',
    labName: 'All Is Well Pathology',
    status: 'available',
    reportFileUrl: null,
    category: 'Blood Tests',
    isDemo: true,
    createdAt: '2026-08-18T09:15:00Z',
    updatedAt: '2026-08-18T12:00:00Z',
  },
  {
    reportId: 'RPT-DEMO-003',
    patientId: 'AIW-DEMO-000124',
    testName: 'Liver Function Test (LFT)',
    testDate: '15 August 2026',
    centreId: 'centre-khandwa',
    centreName: 'All Is Well Diagnostics, Khandwa',
    labId: 'LAB-AIW-002',
    labName: 'All Is Well Pathology',
    status: 'available',
    reportFileUrl: null,
    category: 'Biochemistry',
    isDemo: true,
    createdAt: '2026-08-15T10:00:00Z',
    updatedAt: '2026-08-15T14:00:00Z',
  },
];

// ─── In-memory audit log (prototype only) ─────────────────────────────────────
// In production, this must be persisted to a secure backend audit table.
const _auditLog: ReportAuditEntry[] = [];

// ─── PathologyService ─────────────────────────────────────────────────────────
/**
 * PathologyService — the single abstraction layer between the UI and the
 * pathology/LIS data source.
 *
 * CURRENT IMPLEMENTATION: Returns demo data.
 * FUTURE IMPLEMENTATION:  Replace method bodies with secure API calls to the
 *                         All Is Well Pathology/LIS API. The UI does not change.
 */
export const PathologyService = {
  /**
   * Verify a patient by mobile number and OTP.
   * DEMO: accepts DEMO_MOBILE_NUMBER + DEMO_OTP.
   * PRODUCTION: call secure OTP verification API.
   */
  async verifyPatient(mobile: string, otp: string): Promise<PatientVerificationResult> {
    // Simulate network delay
    await new Promise((r) => setTimeout(r, 800));

    const normalised = mobile.replace(/\s/g, '').replace(/^(\+91|91)/, '');

    if (normalised === DEMO_MOBILE_NUMBER && otp === DEMO_OTP) {
      return { success: true, patient: DEMO_PATIENT_RECORD };
    }

    // PRODUCTION INTEGRATION POINT:
    // const res = await fetch('/api/pathology/verify-patient', {
    //   method: 'POST',
    //   body: JSON.stringify({ mobile, otp }),
    // });
    // return res.json();

    return {
      success: false,
      patient: null,
      error: 'Verification failed. Please check your mobile number and OTP.',
    };
  },

  /**
   * Send OTP to the given mobile number.
   * DEMO: always returns success.
   * PRODUCTION: call secure OTP dispatch API.
   */
  async sendOtp(mobile: string): Promise<{ success: boolean; error?: string }> {
    await new Promise((r) => setTimeout(r, 600));

    // PRODUCTION INTEGRATION POINT:
    // const res = await fetch('/api/auth/send-otp', {
    //   method: 'POST',
    //   body: JSON.stringify({ mobile }),
    // });
    // return res.json();

    return { success: true };
  },

  /**
   * Retrieve available lab reports for a verified patient.
   * DEMO: returns DEMO_LAB_REPORTS for the demo patient.
   * PRODUCTION: call secure backend with patient_id + auth token.
   *
   * SECURITY: The backend MUST verify:
   *   authenticated patient + patient_id + report ownership
   * before returning any report data.
   */
  async getReportsForPatient(patientId: string): Promise<PatientReportsResult> {
    await new Promise((r) => setTimeout(r, 700));

    if (patientId === DEMO_PATIENT_RECORD.patientId) {
      return { success: true, reports: DEMO_LAB_REPORTS };
    }

    // PRODUCTION INTEGRATION POINT:
    // const res = await fetch(`/api/pathology/reports?patientId=${patientId}`, {
    //   headers: { Authorization: `Bearer ${authToken}` },
    // });
    // return res.json();

    return { success: false, reports: [], error: 'No reports found for this patient.' };
  },

  /**
   * Generate a secure report access token (for WhatsApp delivery).
   * DEMO: returns a placeholder token.
   * PRODUCTION: call backend to generate an expiring signed URL / token.
   *
   * SECURITY: Never expose permanent public PDF URLs.
   * The backend must verify patient ownership before generating the token.
   */
  async generateSecureReportAccess(
    patientId: string,
    reportId: string
  ): Promise<{ success: boolean; accessToken?: string; expiresAt?: string; error?: string }> {
    await new Promise((r) => setTimeout(r, 500));

    // PRODUCTION INTEGRATION POINT:
    // const res = await fetch('/api/pathology/secure-access', {
    //   method: 'POST',
    //   body: JSON.stringify({ patientId, reportId }),
    //   headers: { Authorization: `Bearer ${authToken}` },
    // });
    // return res.json();

    return {
      success: true,
      accessToken: `DEMO-TOKEN-${reportId}-${Date.now()}`,
      expiresAt: 'DEMO — expires in 24 hours (production)',
    };
  },

  /**
   * Log a report access event for audit purposes.
   * DEMO: stores in memory only.
   * PRODUCTION: persist to secure audit log table.
   */
  logReportAccess(entry: ReportAuditEntry): void {
    _auditLog.push(entry);
    // PRODUCTION INTEGRATION POINT:
    // fetch('/api/audit/report-access', { method: 'POST', body: JSON.stringify(entry) });
  },

  /** Returns the in-memory audit log (demo only). */
  getAuditLog(): ReportAuditEntry[] {
    return [..._auditLog];
  },
};

// ─── WhatsApp Report Message Builder ─────────────────────────────────────────

/**
 * Builds the official WhatsApp report delivery message.
 * Only minimum information — no actual medical results in the message.
 */
export function buildReportWhatsAppMessage(
  patient: PatientRecord,
  report: LabReport
): string {
  return `ALL IS WELL HOSPITAL

Your laboratory report is ready.

━━━━━━━━━━━━━━━━

PATIENT
Name: ${patient.fullName}
Patient ID: ${patient.patientId}

REPORT
Test: ${report.testName}
Date: ${report.testDate}
Centre: ${report.centreName}

━━━━━━━━━━━━━━━━

I would like to receive my lab report securely.

For your privacy, please do not forward this report.

— All Is Well Experience™`;
}

/**
 * Generates a unique lab report request ID.
 * Format: AIW-LAB-DDMMYY-XXXXX
 */
export function generateLabRequestId(): string {
  const now = new Date();
  const dd = String(now.getDate()).padStart(2, '0');
  const mm = String(now.getMonth() + 1).padStart(2, '0');
  const yy = String(now.getFullYear()).slice(-2);
  const seq = String(Math.floor(Math.random() * 99999) + 1).padStart(5, '0');
  return `AIW-LAB-${dd}${mm}${yy}-${seq}`;
}
